import { AfterViewInit, ApplicationRef, ChangeDetectionStrategy, Component, ElementRef, HostListener, Injector, OnDestroy, TemplateRef, ViewContainerRef, ViewEncapsulation, afterNextRender, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { firstValueFrom } from 'rxjs';

import { AIService } from '@workbuddy/ai/core/ai.service';
import { AGENT_ID } from '../../agent.types';
import type { AgentThread, EnrichedThreadTab, ThreadTab } from '../../agent.types';
import { AgentPanelService, RESERVED_TAB_ID } from '../../services/agent.panel.service';
import { BackgroundStreamManager } from '../../services/background.stream.manager';
import { AgentThreadTabComponent } from '../thread.tab/thread.tab.component';
import { AgentThreadListComponent } from '../thread.list/thread.list.component';

// How long the history popover's thread list stays reusable before a reopen refetches it.
const HISTORY_CACHE_TTL_MS = 15_000;

@Component({
  selector: 'agent-chat-footer',
  standalone: true,
  imports: [AgentThreadTabComponent, AgentThreadListComponent],
  templateUrl: './chat.footer.component.html',
  styleUrls: ['./chat.footer.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AgentChatFooterComponent implements AfterViewInit, OnDestroy {

  private readonly aiService = inject(AIService);
  private readonly elementRef = inject(ElementRef);
  private readonly panelService = inject(AgentPanelService);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(Injector);
  private readonly backgroundManager = inject(BackgroundStreamManager);
  private readonly viewContainerRef = inject(ViewContainerRef);

  // ─── Inputs ─────────────────────────────────────────────────────────────────

  public readonly $tabs = input.required<EnrichedThreadTab[]>({ alias: 'tabs' });
  public readonly $activeTabId = input<string | null>(null, { alias: 'activeTabId' });

  // ─── Outputs ────────────────────────────────────────────────────────────────

  public readonly tabActivated = output<string>();
  public readonly tabClosed = output<string>();
  public readonly newChat = output<void>();
  public readonly openHistory = output<void>();
  public readonly threadSelected = output<{ threadId: string; title: string }>();

  // ─── History Popover State ──────────────────────────────────────────────────

  protected readonly $historyOpen = this.panelService.$historyOpen;
  protected readonly $threadStates = this.backgroundManager.$threadStates;
  protected readonly $threads = signal<AgentThread[]>([]);
  protected readonly $loading = signal(false);
  protected readonly $historyError = signal<string | null>(null);
  // When the popover's thread list was last fetched SUCCESSFULLY. Reopening within
  // HISTORY_CACHE_TTL_MS reuses what's already on screen: the list barely changes minute to minute,
  // and every avoidable fetch is budget taken from the shared per-user AI rate limit that sending
  // messages also draws on. Zero means never fetched, which is the only state that forces a load.
  private threadsFetchedAt = 0;
  // The in-flight history fetch, shared by any reopen that happens while it is still running.
  private historyRequest: Promise<void> | null = null;

  protected readonly historyTpl = viewChild<TemplateRef<unknown>>('historyPopoverTpl');
  private readonly threadListRef = viewChild(AgentThreadListComponent);

  // ─── Portal for rendering popover on document.body ─────────────────────────

  private portalOutlet: DomPortalOutlet | null = null;
  private portalHost: HTMLDivElement | null = null;
  private portal: TemplatePortal | null = null;

  private readonly _syncPortal = effect(() => {

    const open = this.$historyOpen();
    // Need to defer to afterViewInit so template ref is available
    if (!this.portalOutlet) return;
    if (open) {
      this.attachPortal();
    } else {
      this.detachPortal();
    }
  });

  // ─── Reserved Tabs ─────────────────────────────────────────────────────────

  protected readonly newChatTab: ThreadTab = { threadId: RESERVED_TAB_ID.NEW_CHAT, title: 'Ask Workbuddy' };
  protected readonly historyTab: ThreadTab = { threadId: RESERVED_TAB_ID.HISTORY, title: 'History' };
  protected readonly $newChatActive = this.panelService.$newChatOpen;

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  public ngAfterViewInit(): void {

    this.portalHost = document.createElement('div');
    document.body.appendChild(this.portalHost);
    this.portalOutlet = new DomPortalOutlet(this.portalHost, this.appRef, this.injector);

    // If history was already open before view init, attach now
    if (this.$historyOpen()) {
      this.attachPortal();
    }
  }

  public ngOnDestroy(): void {

    this.detachPortal();
    this.portalHost?.remove();
  }

  private attachPortal(): void {

    if (this.portalOutlet?.hasAttached()) return;
    const tpl = this.historyTpl();
    if (!tpl || !this.portalOutlet) return;
    this.portal = new TemplatePortal(tpl, this.viewContainerRef);
    this.portalOutlet.attach(this.portal);
  }

  private detachPortal(): void {

    if (this.portalOutlet?.hasAttached()) {
      this.portalOutlet.detach();
    }
    this.portal = null;
  }

  // ─── History Popover Actions ────────────────────────────────────────────────

  protected async toggleHistory(): Promise<void> {

    this.panelService.toggleHistory();
    this.openHistory.emit();

    if (this.$historyOpen()) {
      afterNextRender(() => this.threadListRef()?.focusSearch(), { injector: this.injector });
      void this.loadHistory();
    }
  }

  /**
   * Fetch the history list, unless a recent result is still good or a fetch is already running.
   *
   * Freshness is judged on when a fetch last SUCCEEDED, not on whether it returned anything — a user
   * with no chats yet has a perfectly valid empty result, and treating that as "nothing cached" made
   * every reopen re-request it. New users were the ones paying most for the missing cache.
   */
  private async loadHistory(): Promise<void> {

    if (this.threadsFetchedAt && Date.now() - this.threadsFetchedAt < HISTORY_CACHE_TTL_MS) return;

    // Reopening while the first request is still out shares that request rather than starting a
    // second one — the slow responses worth de-duplicating are exactly the ones a user re-clicks during.
    if (this.historyRequest) return this.historyRequest;

    this.$loading.set(true);
    this.$historyError.set(null);

    const request = firstValueFrom(this.aiService.listThreads(AGENT_ID, 0, 100))
      .then(response => {
        this.$threads.set(response.threads);
        this.threadsFetchedAt = Date.now();
      })
      .catch((error: unknown) => {
        // Distinguish "you have no chats" from "we couldn't load them". Blanking the list on a
        // failure — a rate limit especially — reads as an empty history, which is a lie.
        const rateLimited = (error as { status?: number } | null)?.status === 429;
        this.$historyError.set(rateLimited ? 'Too many requests — try again in a moment.' : 'Could not load your chats.');
      })
      .finally(() => {
        this.historyRequest = null;
        this.$loading.set(false);
      });

    this.historyRequest = request;
    return request;
  }

  protected onSelectThread(event: { threadId: string; title: string }): void {

    this.threadSelected.emit(event);
  }

  protected async onDeleteThread(threadId: string): Promise<void> {

    try {
      await firstValueFrom(this.aiService.deleteThread(AGENT_ID, threadId));
      this.$threads.update(threads => threads.filter(t => t.id !== threadId));
      // Also close the tab if it's open
      this.panelService.closeTab(threadId);
    } catch {
      // silently fail
    }
  }

  protected onNewChat(): void {

    this.newChat.emit();
  }

  protected onTabActivated(threadId: string): void {

    this.tabActivated.emit(threadId);
  }

  protected onTabClosed(threadId: string): void {

    this.tabClosed.emit(threadId);
  }

  // ─── Close Popover on Outside Click or Escape ───────────────────────────────

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {

    if (!this.$historyOpen()) return;
    // Check if click is inside the footer or the portal host (which is on body)
    if (this.elementRef.nativeElement.contains(event.target)) return;
    if (this.portalHost?.contains(event.target as Node)) return;
    this.panelService.toggleHistory();
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {

    if (this.$historyOpen()) {
      this.panelService.toggleHistory();
    }
  }
}
